import React, { useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { ArrowLeft, LayoutGrid, ShoppingBag } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { useGetProductsQuery } from "../../../productApi/productAPI";
import {
  addToCart,
  increaseQuantity,
} from "../../../store/cartSlice/cartSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks/hooks";
import { Product } from "../../../types/product";
import StoreProductCard from "../StoreProductCard";
import {
  normalizeCategoryLabel,
  getCategoryVisual,
} from "../../../services/category/categoryVisuals";
import ProductCarouselTwoColumn from "../../Carousel/ProductCarouselTwoColumn";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";

const cardsBorderRadii = "8px";

const slugifyCategory = (value: string = "") =>
  normalizeCategoryLabel(value)
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");

const unslugifyCategory = (value: string = "") =>
  value
    .replace(/-/g, " ")
    .replace(/\band\b/g, "&")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getProductImage = (product: any) =>
  product?.thumbnail ||
  (Array.isArray(product?.images) && product.images[0]) ||
  product?.imageUrl ||
  "https://picsum.photos/seed/fallback-category/300/300";

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");
  if (cleaned.length !== 6) return `rgba(100,116,139,${alpha})`;

  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CategoryProductsPage = () => {
  const { categorySlug = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cart = useAppSelector((state) => state.cart);
  const { theme } = useCustomTheme();

  const { data: products = [], isLoading } = useGetProductsQuery();

  const groupedCategories = useMemo(() => {
    const grouped = new Map<string, any[]>();

    (products || []).forEach((product: any) => {
      const rawCategory = normalizeCategoryLabel(
        product?.category || "General",
      );
      if (!grouped.has(rawCategory)) grouped.set(rawCategory, []);
      grouped.get(rawCategory)?.push(product);
    });

    const result = Array.from(grouped.entries()).map(([title, items]) => {
      const visual = getCategoryVisual(title);
      const Icon = visual.icon;
      const slug = slugifyCategory(title);

      return {
        id: slug,
        title,
        slug,
        image: getProductImage(items[0]),
        description: `Browse ${title}`,
        meta: `${items.length} product${items.length !== 1 ? "s" : ""}`,
        navigateTo: `/shop/category/${slug}`,
        leftIcon: <Icon size={16} />,
        leftIconColor: visual.color,
        leftIconBg: hexToRgba(visual.color, 0.14),
        items,
      };
    });

    console.log("Raw products:", products);
    console.log("Grouped categories for UI:", result);

    return result.sort((a, b) => a.title.localeCompare(b.title));
  }, [products]);

  const currentCategory = useMemo(() => {
    return groupedCategories.find((cat) => cat.slug === categorySlug);
  }, [groupedCategories, categorySlug]);

  const filteredProducts = useMemo(() => {
    return currentCategory?.items || [];
  }, [currentCategory]);

  const otherCategories = useMemo(() => {
    const result = groupedCategories.filter((cat) => cat.slug !== categorySlug);
    console.log("Current category:", currentCategory);
    console.log("Other categories:", result);
    return result;
  }, [groupedCategories, categorySlug, currentCategory]);

  const categoryTitle =
    currentCategory?.title || unslugifyCategory(categorySlug);
  const categoryDescription = `Browse premium ${categoryTitle.toLowerCase()} products, add items to your cart, and continue exploring other categories below.`;

  const currentCategoryVisual = getCategoryVisual(categoryTitle);
  const CurrentIcon = currentCategoryVisual.icon;

  const handleAddToCart = (product: Product) => {
    const existing = cart.items.find((item) => item._id === product._id);
    if (!existing) {
      dispatch(addToCart(product));
    } else {
      dispatch(increaseQuantity(product._id!));
    }
  };

  if (isLoading) {
    return (
      <Flex minH="300px" justify="center" align="center">
        <GlobalSpinner />
      </Flex>
    );
  }

  return (
    <Box
      bg={theme.background}
      minH="100vh"
      px={{ base: 3, md: 6 }}
      py={{ base: 4, md: 6 }}
    >
      <Box
        maxW="1450px"
        mx="auto"
        bg={theme.surface}
        borderRadius={cardsBorderRadii}
        boxShadow={theme.shadowMd}
        overflow="hidden"
        border={`1px solid ${theme.border}`}
      >
        <Box
          px={{ base: 4, md: 6 }}
          py={{ base: 5, md: 7 }}
          borderBottom={`1px solid ${theme.border}`}
          bg={theme.gradients?.shellBg || theme.surface}
        >
          <Button
            mb={5}
            variant="ghost"
            leftIcon={<ArrowLeft size={18} />}
            color={theme.text}
            onClick={() => navigate("/shop")}
            _hover={{
              bg: theme.surfaceAlt,
            }}
            borderRadius={theme.radii.sm}
          >
            Back to Store
          </Button>

          <Flex
            direction={{ base: "column", lg: "row" }}
            justify="space-between"
            align={{ base: "flex-start", lg: "center" }}
            gap={4}
          >
            <Box>
              <HStack spacing={3} mb={3}>
                <Flex
                  w="46px"
                  h="46px"
                  borderRadius="4px"
                  align="center"
                  justify="center"
                  bg={hexToRgba(currentCategoryVisual.color, 0.14)}
                  color={currentCategoryVisual.color}
                  border={`1px solid ${hexToRgba(currentCategoryVisual.color, 0.25)}`}
                >
                  <CurrentIcon size={22} />
                </Flex>

                <Badge
                  borderRadius="4px"
                  px={3}
                  py={1}
                  textTransform="none"
                  fontSize="xs"
                  bg={theme.surfaceAlt}
                  color={theme.textMuted}
                  border={`1px solid ${theme.border}`}
                >
                  Category
                </Badge>
              </HStack>

              <Heading color={theme.text} fontSize={{ base: "2xl", md: "3xl" }}>
                {categoryTitle}
              </Heading>

              <Text mt={2} color={theme.textMuted} maxW="780px">
                {categoryDescription}
              </Text>
            </Box>

            <Flex gap={3} wrap="wrap">
              <Box
                px={4}
                py={3}
                borderRadius={cardsBorderRadii}
                bg={theme.surfaceAlt}
                border={`1px solid ${theme.border}`}
              >
                <Text color={theme.textMuted} fontSize="xs" mb={1}>
                  Products
                </Text>
                <Text color={theme.text} fontWeight="800" fontSize="lg">
                  {filteredProducts.length}
                </Text>
              </Box>

              <Box
                px={4}
                py={3}
                borderRadius={cardsBorderRadii}
                bg={theme.surfaceAlt}
                border={`1px solid ${theme.border}`}
              >
                <Text color={theme.textMuted} fontSize="xs" mb={1}>
                  Other categories
                </Text>
                <Text color={theme.text} fontWeight="800" fontSize="lg">
                  {otherCategories.length}
                </Text>
              </Box>
            </Flex>
          </Flex>
        </Box>

        <Box px={{ base: 4, md: 6 }} py={{ base: 5, md: 7 }}>
          {filteredProducts.length === 0 ? (
            <Box
              p={8}
              textAlign="center"
              borderRadius={cardsBorderRadii}
              bg={theme.surfaceAlt}
              border={`1px solid ${theme.border}`}
            >
              <Text color={theme.text} fontWeight="700" fontSize="lg">
                No products found in this category
              </Text>
              <Text color={theme.textMuted} mt={2}>
                Try another category from below.
              </Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
              {filteredProducts.map((product: any) => (
                <StoreProductCard
                  key={product._id}
                  product={product}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </SimpleGrid>
          )}
        </Box>

        <Box
          px={{ base: 4, md: 6 }}
          pb={{ base: 6, md: 8 }}
          pt={{ base: 1, md: 2 }}
        >
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={3}
            mb={4}
          >
            <Box>
              <Heading fontSize={{ base: "xl", md: "2xl" }} color={theme.text}>
                Explore Other Categories
              </Heading>
              <Text mt={1} color={theme.textMuted}>
                Quickly jump to another department without leaving the store
                flow.
              </Text>
            </Box>

            <HStack
              spacing={2}
              px={3}
              py={2}
              borderRadius={cardsBorderRadii}
              bg={theme.surfaceAlt}
              border={`1px solid ${theme.border}`}
              color={theme.textMuted}
            >
              <LayoutGrid size={16} />
              <Text fontSize="sm">{otherCategories.length} categories</Text>
            </HStack>
          </Flex>

          <ProductCarouselTwoColumn
            products={otherCategories}
            slidesToShow={{ base: 2, sm: 3, md: 5, lg: 6, xl: 7 }}
            itemRoutePrefix="/shop/category"
          />
        </Box>

        <Box px={{ base: 4, md: 6 }} pb={{ base: 6, md: 8 }}>
          <Flex
            mt={2}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
            border={`1px solid ${theme.border}`}
            px={{ base: 4, md: 5 }}
            py={{ base: 4, md: 5 }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <HStack align="flex-start" spacing={3}>
              <Flex
                w="42px"
                h="42px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={theme.gradients?.primaryButton || theme.primary}
                color={theme.textOnPrimary}
              >
                <ShoppingBag size={18} />
              </Flex>
              <Box>
                <Text color={theme.text} fontWeight="700">
                  Continue shopping smoothly
                </Text>
                <Text color={theme.textMuted} fontSize="sm">
                  Products stay add-to-cart ready, and category browsing remains
                  fast and intuitive.
                </Text>
              </Box>
            </HStack>

            <Button
              onClick={() => navigate("/shop")}
              color={theme.textOnPrimary}
              bgImage={theme.gradients?.primaryButton || theme.primary}
              _hover={{
                bgImage:
                  theme.gradients?.primaryButtonHover || theme.primaryHover,
                transform: "translateY(-1px)",
              }}
              borderRadius={theme.radii.sm}
            >
              View full store
            </Button>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryProductsPage;
